import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Typography, 
  Table, 
  Button, 
  Tag, 
  Space, 
  Spin, 
  Alert,
  Statistic,
  Row,
  Col
} from 'antd'
import { 
  EyeOutlined, 
  CreditCardOutlined,
  DollarOutlined,
  FileTextOutlined,
  CalendarOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { customerApi } from '../services/customerApi'
import dayjs from 'dayjs'

const { Title, Text } = Typography

interface Invoice {
  id: string
  invoiceNumber: string
  totalAmount: number
  status: 'DRAFT' | 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED'
  dueAt: string
  createdAt: string
  job?: {
    id: string
    title: string
  }
}

export const InvoiceList: React.FC = () => {
  const navigate = useNavigate()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadInvoices()
  }, [])

  const loadInvoices = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await customerApi.getInvoices()
      setInvoices(data.invoices || [])
    } catch (err: any) {
      console.error('Failed to load invoices:', err)
      setError(err.response?.data?.message || 'Failed to load invoices')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID': return 'success'
      case 'SENT': return 'processing'
      case 'OVERDUE': return 'error'
      case 'DRAFT': return 'default'
      case 'CANCELLED': return 'default'
      default: return 'default'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PAID': return 'Paid'
      case 'SENT': return 'Sent'
      case 'OVERDUE': return 'Overdue'
      case 'DRAFT': return 'Draft'
      case 'CANCELLED': return 'Cancelled'
      default: return status
    }
  }

  const columns = [
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      render: (text: string, record: Invoice) => (
        <Space direction="vertical" size="small">
          <Text strong>{text}</Text>
          {record.job && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.job.title}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => (
        <Statistic
          value={amount}
          precision={2}
          prefix="$"
          valueStyle={{ fontSize: '16px' }}
        />
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: 'Due Date',
      dataIndex: 'dueAt',
      key: 'dueAt',
      render: (date: string) => (
        <Space>
          <CalendarOutlined />
          {date ? dayjs(date).format('MMM D, YYYY') : 'No due date'}
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text: any, record: Invoice) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/invoices/${record.id}`)}
            size="small"
          >
            View
          </Button>
          {record.status !== 'PAID' && record.status !== 'CANCELLED' && (
            <Button
              type="primary"
              icon={<CreditCardOutlined />}
              onClick={() => navigate(`/invoices/${record.id}/pay`)}
              size="small"
            >
              Pay Now
            </Button>
          )}
        </Space>
      )
    }
  ]

  // Calculate summary statistics
  const totalAmount = invoices.reduce((sum, invoice) => sum + Number(invoice.totalAmount || 0), 0)
  const paidAmount = invoices
    .filter(invoice => invoice.status === 'PAID')
    .reduce((sum, invoice) => sum + Number(invoice.totalAmount || 0), 0)
  const unpaidAmount = totalAmount - paidAmount
  const overdueInvoices = invoices.filter(invoice => 
    invoice.status === 'OVERDUE' || 
    (invoice.status === 'SENT' && invoice.dueAt && dayjs(invoice.dueAt).isBefore(dayjs()))
  )

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px' }}>Loading invoices...</p>
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Invoices"
        description={error}
        type="error"
        showIcon
        action={
          <Button type="primary" onClick={loadInvoices}>
            Retry
          </Button>
        }
      />
    )
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <FileTextOutlined /> My Invoices
        </Title>
        <Text type="secondary">
          View and pay your invoices
        </Text>
      </div>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Amount"
              value={totalAmount}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Paid Amount"
              value={paidAmount}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Outstanding"
              value={unpaidAmount}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: unpaidAmount > 0 ? '#faad14' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Overdue Alert */}
      {overdueInvoices.length > 0 && (
        <Alert
          message={`You have ${overdueInvoices.length} overdue invoice${overdueInvoices.length > 1 ? 's' : ''}`}
          description="Please pay your overdue invoices to avoid service interruption."
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
          action={
            <Button size="small" type="primary">
              Pay Overdue
            </Button>
          }
        />
      )}

      {/* Invoices Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={invoices}
          rowKey="id"
          pagination={{
            total: invoices.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} invoices`
          }}
          locale={{
            emptyText: (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <FileTextOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                <p style={{ marginTop: '16px', color: '#999' }}>
                  No invoices found
                </p>
              </div>
            )
          }}
        />
      </Card>
    </div>
  )
}
